import { supabase } from '../utils/supabase';
import { isSelectableMatchupSearchPlayer } from '../utils/clubPlayerSearchUtils';
import {
  applyMatchupDateFilters,
  applyMatchupGameTypeFilter,
  filterGameGroupsByGameTypeIds,
  resolveMatchupGameTypeIds,
} from '../utils/matchupFilters';

const MATCHUP_SEARCH_PLAYER_FIELDS =
  'id, first_name, last_name, gender, is_guest, is_active, deletion_requested_at, merged_at, merged_into_user_id';

/**
 * Fetch player details for specific player IDs
 * @param {Array} playerIds - Array of player IDs
 * @returns {Promise<Object>} Player data mapped by player ID
 */
const fetchPlayerDetails = async (playerIds) => {
  if (!playerIds || playerIds.length === 0) return {};

  try {
    const { data: players, error } = await supabase
      .from('players')
      .select('id, first_name, last_name, gender')
      .in('id', playerIds);

    if (error) {
      console.error('Error fetching player details:', error);
      return {};
    }

    // Organize player data by ID
    const playerMap = {};
    players.forEach(player => {
      const firstName = player.first_name || '';
      const lastName = player.last_name || '';
      playerMap[player.id] = {
        id: player.id,
        name: `${firstName} ${lastName}`.trim() || firstName || 'Unknown',
        firstName,
        lastName,
        first_name: firstName,
        last_name: lastName,
        gender: player.gender
      };
    });

    return playerMap;
  } catch (error) {
    console.error('Error in fetchPlayerDetails:', error);
    return {};
  }
};

/**
 * Fetch ELO data for specific games and players
 * @param {Array} gameIds - Array of session_game_ids
 * @param {string} player1Id - First player's ID
 * @param {string} player2Id - Second player's ID
 * @param {string} clubId - Club ID
 * @returns {Promise<Object>} ELO data mapped by game ID and player ID
 */
const fetchEloDataForGames = async (gameIds, player1Id, player2Id, clubId) => {
  if (!gameIds || gameIds.length === 0) return {};

  try {
    const { data: eloData, error } = await supabase
      .from('elo_history')
      .select(`
        session_game_id,
        player_id,
        old_value,
        new_value,
        elo_change,
        pair_type
      `)
      .in('session_game_id', gameIds)
      .in('player_id', [player1Id, player2Id])
      .eq('club_id', clubId);

    if (error) {
      console.error('Error fetching ELO data:', error);
      return {};
    }

    // Organize ELO data by game ID and player ID
    const eloMap = {};
    eloData.forEach(elo => {
      if (!eloMap[elo.session_game_id]) {
        eloMap[elo.session_game_id] = {};
      }
      eloMap[elo.session_game_id][elo.player_id] = {
        preGameElo: elo.old_value,
        postGameElo: elo.new_value,
        eloChange: elo.elo_change,
        pairType: elo.pair_type
      };
    });

    return eloMap;
  } catch (error) {
    console.error('Error in fetchEloDataForGames:', error);
    return {};
  }
};

/**
 * Fetch ELO data for team games (4 players)
 * @param {Array} gameIds - Array of session_game_ids
 * @param {Array} playerIds - Array of 4 player IDs
 * @param {string} clubId - Club ID
 * @returns {Promise<Object>} ELO data mapped by game ID and player ID
 */
const fetchTeamEloDataForGames = async (gameIds, playerIds, clubId) => {
  if (!gameIds || gameIds.length === 0) return {};

  try {
    const { data: eloData, error } = await supabase
      .from('elo_history')
      .select(`
        session_game_id,
        player_id,
        old_value,
        new_value,
        elo_change,
        pair_type
      `)
      .in('session_game_id', gameIds)
      .in('player_id', playerIds)
      .eq('club_id', clubId);

    if (error) {
      console.error('Error fetching team ELO data:', error);
      return {};
    }

    // Organize ELO data by game ID and player ID
    const eloMap = {};
    eloData.forEach(elo => {
      if (!eloMap[elo.session_game_id]) {
        eloMap[elo.session_game_id] = {};
      }
      eloMap[elo.session_game_id][elo.player_id] = {
        preGameElo: elo.old_value,
        postGameElo: elo.new_value,
        eloChange: elo.elo_change,
        pairType: elo.pair_type
      };
    });

    return eloMap;
  } catch (error) {
    console.error('Error in fetchTeamEloDataForGames:', error);
    return {};
  }
};

/**
 * Fetch head-to-head record between two individual players
 * @param {string} player1Id - First player's ID
 * @param {string} player2Id - Second player's ID
 * @param {string} clubId - Club ID to filter by
 * @param {string} seasonId - Optional season ID to filter by
 * @param {string} gameType - Optional legacy pair-type filter ('mixed', 'levels', 'singles', 'all')
 * @param {Object} [dateOpts] - { dateFrom, dateTo, balancedOnly, gameTypeIds }
 * @returns {Promise<Object>} H2H record data
 */
export const fetchIndividualHeadToHead = async (
  player1Id,
  player2Id,
  clubId,
  seasonId = null,
  gameType = 'all',
  dateOpts = {}
) => {
  const { dateFrom = null, dateTo = null, balancedOnly = false, gameTypeIds } = dateOpts;
  const resolvedGameTypeIds = resolveMatchupGameTypeIds({
    gameTypeIds,
    gameType,
    balancedOnly,
  });
  try {
    console.log('fetchIndividualHeadToHead called with:', {
      player1Id,
      player2Id,
      clubId,
      seasonId,
      gameType,
      dateFrom,
      dateTo,
    });
    
    // First, find all games where both players participated
    let query = supabase
      .from('players_session_games')
      .select(`
        *,
        session_games!inner(
          id,
          session_id,
          club_id,
          season_id,
          created_at,
          team_1_score,
          team_2_score,
          winning_team,
          game_type_id
        )
      `)
      .or(`player_id.eq.${player1Id},player_id.eq.${player2Id}`)
      .eq('session_games.club_id', clubId);

    if (seasonId) {
      query = query.eq('session_games.season_id', seasonId);
    }
    query = applyMatchupDateFilters(query, dateFrom, dateTo);
    query = applyMatchupGameTypeFilter(query, resolvedGameTypeIds);

    const { data: allGames, error } = await query;
    console.log('Query result:', { allGames: allGames?.length, error });
    // Debug: Check if session_id is in the data
    if (allGames && allGames.length > 0) {
      console.log('Sample game session_games:', allGames[0].session_games);
    }

    if (error) throw error;

    // Group games by session_game_id to find games where both players participated
    const gameGroups = {};
    allGames.forEach(game => {
      const gameId = game.session_game_id;
      if (!gameGroups[gameId]) {
        gameGroups[gameId] = [];
      }
      gameGroups[gameId].push(game);
    });

    // Filter to games where both players participated on OPPOSING teams
    const h2hGameIds = Object.values(gameGroups).filter(games => {
      if (games.length !== 2) return false;
      
      const player1Game = games.find(g => g.player_id === player1Id);
      const player2Game = games.find(g => g.player_id === player2Id);
      
      // Both players must be present AND on different teams
      return player1Game && player2Game && 
             player1Game.team_number !== player2Game.team_number;
    }).map(games => games[0].session_game_id);

    console.log('H2H game IDs found:', h2hGameIds);

    // Now get ALL players in these specific games (including teammates)
    let allPlayersQuery = supabase
      .from('players_session_games')
      .select(`
        *,
        session_games!inner(
          id,
          session_id,
          club_id,
          season_id,
          created_at,
          team_1_score,
          team_2_score,
          winning_team,
          game_type_id
        )
      `)
      .in('session_game_id', h2hGameIds)
      .eq('session_games.club_id', clubId);

    if (seasonId) {
      allPlayersQuery = allPlayersQuery.eq('session_games.season_id', seasonId);
    }
    allPlayersQuery = applyMatchupDateFilters(allPlayersQuery, dateFrom, dateTo);
    allPlayersQuery = applyMatchupGameTypeFilter(allPlayersQuery, resolvedGameTypeIds);

    const { data: allPlayersInGames, error: allPlayersError } = await allPlayersQuery;
    console.log('All players in games:', { allPlayersInGames: allPlayersInGames?.length, allPlayersError });

    if (allPlayersError) throw allPlayersError;

    // Group by game ID again, but now with all players
    const gameGroupsWithAllPlayers = {};
    allPlayersInGames.forEach(game => {
      const gameId = game.session_game_id;
      if (!gameGroupsWithAllPlayers[gameId]) {
        gameGroupsWithAllPlayers[gameId] = [];
      }
      gameGroupsWithAllPlayers[gameId].push(game);
    });

    let filteredGames = Object.values(gameGroupsWithAllPlayers);
    filteredGames = filterGameGroupsByGameTypeIds(filteredGames, resolvedGameTypeIds);

    // Fetch ELO data for all games
    const gameIds = filteredGames.map(games => games[0].session_game_id);
    const eloData = await fetchEloDataForGames(gameIds, player1Id, player2Id, clubId);

    // Get all unique player IDs from all games to fetch player details
    const allPlayerIds = new Set();
    filteredGames.forEach(games => {
      games.forEach(game => {
        allPlayerIds.add(game.player_id);
      });
    });
    const playerDetails = await fetchPlayerDetails(Array.from(allPlayerIds));

    // Calculate H2H statistics
    const h2hStats = calculateIndividualH2HStats(filteredGames, player1Id, player2Id);

    const result = {
      player1Id,
      player2Id,
      totalGames: filteredGames.length,
      ...h2hStats,
      games: filteredGames.map(games => {
        const player1Game = games.find(g => g.player_id === player1Id);
        const player2Game = games.find(g => g.player_id === player2Id);
        const gameId = games[0].session_game_id;
        
        // Get ELO data for this specific game
        const gameEloData = eloData[gameId] || {};
        const player1Elo = gameEloData[player1Id];
        const player2Elo = gameEloData[player2Id];
        
        // Get teammate information
        // For Individual H2H, we need to find the other player on the same team
        const player1Team = games.filter(g => g.team_number === player1Game?.team_number && g.player_id !== player1Id);
        const player2Team = games.filter(g => g.team_number === player2Game?.team_number && g.player_id !== player2Id);
        
        // If no teammates found, this might be a singles game or the data structure is different
        // Let's also check if we can find teammates by looking at all players in the game
        const allPlayersInGame = games.map(g => ({ player_id: g.player_id, team_number: g.team_number }));
        const player1TeammateFromAll = allPlayersInGame.find(p => p.team_number === player1Game?.team_number && p.player_id !== player1Id);
        const player2TeammateFromAll = allPlayersInGame.find(p => p.team_number === player2Game?.team_number && p.player_id !== player2Id);
        
        // Debug logging removed - teammate functionality working
        
        // Try both approaches to find teammates
        const player1Teammate = player1Team.length > 0 ? playerDetails[player1Team[0].player_id] : 
                               (player1TeammateFromAll ? playerDetails[player1TeammateFromAll.player_id] : null);
        const player2Teammate = player2Team.length > 0 ? playerDetails[player2Team[0].player_id] : 
                               (player2TeammateFromAll ? playerDetails[player2TeammateFromAll.player_id] : null);
        
        const gameData = {
          gameId: gameId,
          date: games[0].session_games.created_at,
          sessionId: games[0].session_games?.session_id || null,
          session_id: games[0].session_games?.session_id || null,
          club_id: games[0].session_games?.club_id || null,
          clubId: games[0].session_games?.club_id || null,
          pairType: games[0].pair_type,
          gameTypeId: games[0].session_games?.game_type_id ?? null,
          player1Id: player1Id,
          player2Id: player2Id,
          player1Result: player1Game?.result,
          player2Result: player2Game?.result,
          player1TeamNumber: player1Game?.team_number,
          player2TeamNumber: player2Game?.team_number,
          score: `${games[0].session_games.team_1_score}-${games[0].session_games.team_2_score}`,
          winningTeam: games[0].session_games.winning_team,
          // Teammate information
          player1Teammate: player1Teammate,
          player2Teammate: player2Teammate,
          // ELO data
          player1PreGameElo: player1Elo?.preGameElo || null,
          player1EloChange: player1Elo?.eloChange || null,
          player2PreGameElo: player2Elo?.preGameElo || null,
          player2EloChange: player2Elo?.eloChange || null,
          // Game intensity calculation
          scoreDifference: Math.abs(games[0].session_games.team_1_score - games[0].session_games.team_2_score),
          isCloseGame: Math.abs(games[0].session_games.team_1_score - games[0].session_games.team_2_score) <= 3
        };
        
        // Debug logging removed - teammate functionality working
        
        return gameData;
      })
    };

    console.log('fetchIndividualHeadToHead returning:', result);
    return result;
  } catch (error) {
    console.error('Error fetching individual head-to-head:', error);
    throw error;
  }
};

/**
 * Fetch head-to-head record between two partnerships
 * @param {string} partnership1Id - First partnership ID
 * @param {string} partnership2Id - Second partnership ID
 * @param {string} clubId - Club ID to filter by
 * @param {string} seasonId - Optional season ID to filter by
 * @returns {Promise<Object>} Partnership H2H record data
 */
export const fetchPartnershipHeadToHead = async (partnership1Id, partnership2Id, clubId, seasonId = null) => {
  try {
    // Get partnership details
    const { data: partnerships, error: partnershipsError } = await supabase
      .from('partnerships_with_stats')
      .select('*')
      .in('id', [partnership1Id, partnership2Id]);

    if (partnershipsError) throw partnershipsError;

    if (partnerships.length !== 2) {
      throw new Error('Both partnerships must exist');
    }

    const partnership1 = partnerships.find(p => p.id === partnership1Id);
    const partnership2 = partnerships.find(p => p.id === partnership2Id);

    // Get games for both partnerships
    const { data: partnership1Games, error: games1Error } = await supabase
      .from('partnership_games')
      .select(`
        *,
        session_games!inner(
          id,
          session_id,
          club_id,
          season_id,
          created_at,
          team_1_score,
          team_2_score,
          winning_team
        )
      `)
      .eq('partnership_id', partnership1Id);

    const { data: partnership2Games, error: games2Error } = await supabase
      .from('partnership_games')
      .select(`
        *,
        session_games!inner(
          id,
          session_id,
          club_id,
          season_id,
          created_at,
          team_1_score,
          team_2_score,
          winning_team
        )
      `)
      .eq('partnership_id', partnership2Id);

    if (games1Error || games2Error) throw games1Error || games2Error;

    // Find games where both partnerships played against each other
    const h2hGames = [];
    partnership1Games.forEach(game1 => {
      const matchingGame = partnership2Games.find(game2 => 
        game2.session_game_id === game1.session_game_id
      );
      if (matchingGame) {
        h2hGames.push({
          gameId: game1.session_game_id,
          date: game1.session_games.created_at,
          partnership1Score: game1.session_games.team_1_score,
          partnership2Score: game1.session_games.team_2_score,
          winningTeam: game1.session_games.winning_team,
          partnership1Won: game1.session_games.winning_team === 1
        });
      }
    });

    // Filter by club and season
    let filteredGames = h2hGames;
    if (clubId) {
      filteredGames = filteredGames.filter(game => 
        partnership1Games.find(g => g.session_game_id === game.gameId)?.session_games.club_id === clubId
      );
    }
    if (seasonId) {
      filteredGames = filteredGames.filter(game => 
        partnership1Games.find(g => g.session_game_id === game.gameId)?.session_games.season_id === seasonId
      );
    }

    // Calculate H2H statistics
    const partnership1Wins = filteredGames.filter(g => g.partnership1Won).length;
    const partnership2Wins = filteredGames.length - partnership1Wins;

    return {
      partnership1Id,
      partnership2Id,
      partnership1,
      partnership2,
      totalGames: filteredGames.length,
      partnership1Wins,
      partnership2Wins,
      partnership1WinRate: filteredGames.length > 0 ? (partnership1Wins / filteredGames.length) * 100 : 0,
      partnership2WinRate: filteredGames.length > 0 ? (partnership2Wins / filteredGames.length) * 100 : 0,
      games: filteredGames
    };
  } catch (error) {
    console.error('Error fetching partnership head-to-head:', error);
    throw error;
  }
};

/**
 * Players who have played in the given clubs, for matchup record search.
 * Archived, soft-deleted, merged, and club-removed guests are omitted.
 * @param {string} currentPlayerId
 * @param {string[]} clubIds
 * @returns {Promise<Array>}
 */
export const fetchMatchupSearchPlayers = async (currentPlayerId, clubIds) => {
  const ids = (clubIds || []).filter(Boolean);
  if (!currentPlayerId || ids.length === 0) return [];

  const { data: playerGames, error: gamesError } = await supabase
    .from('players_session_games')
    .select('player_id, session_games!inner(club_id)')
    .neq('player_id', currentPlayerId)
    .in('session_games.club_id', ids);

  if (gamesError) throw gamesError;

  const uniquePlayerIds = [
    ...new Set((playerGames || []).map((game) => game.player_id).filter(Boolean)),
  ];
  if (uniquePlayerIds.length === 0) return [];

  const [{ data: players, error: playersError }, { data: memberships, error: membershipError }] =
    await Promise.all([
      supabase.from('players').select(MATCHUP_SEARCH_PLAYER_FIELDS).in('id', uniquePlayerIds),
      supabase
        .from('players_club')
        .select('player_id')
        .in('player_id', uniquePlayerIds)
        .in('club_id', ids)
        .eq('is_active', true),
    ]);

  if (playersError) throw playersError;
  if (membershipError) throw membershipError;

  const activeMemberIds = new Set((memberships || []).map((row) => row.player_id));

  return (players || [])
    .filter((player) =>
      isSelectableMatchupSearchPlayer(player, {
        hasActiveClubMembership: activeMemberIds.has(player.id),
      })
    )
    .map((player) => ({
      id: player.id,
      first_name: player.first_name,
      last_name: player.last_name,
      gender: player.gender,
      name: getPlayerDisplayName(player),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Get list of players for H2H selection
 * @param {string} currentPlayerId - Current player's ID
 * @param {string} clubId - Club ID to filter by
 * @returns {Promise<Array>} List of potential opponents
 */
export const fetchPotentialOpponents = async (currentPlayerId, clubId) => {
  try {
    return await fetchMatchupSearchPlayers(currentPlayerId, clubId ? [clubId] : []);
  } catch (error) {
    console.error('Error fetching potential opponents:', error);
    throw error;
  }
};

/**
 * Load club games for matchup highlight lists (best with / best against).
 * Aggregation is done client-side so the min-games control can re-rank without refetching.
 */
export const fetchClubMatchupHighlightGames = async (
  currentPlayerId,
  clubId,
  { seasonId = null, dateFrom = null, dateTo = null, balancedOnly = true, gameTypeIds } = {}
) => {
  if (!currentPlayerId || !clubId) {
    return { games: [], hasActiveClubMembershipByPlayerId: {} };
  }
  const resolvedGameTypeIds = resolveMatchupGameTypeIds({
    gameTypeIds,
    balancedOnly,
  });

  let query = supabase
    .from('players_session_games')
    .select(`
      session_game_id,
      session_games!inner(
        id,
        club_id,
        season_id,
        created_at,
        game_type_id
      )
    `)
    .eq('player_id', currentPlayerId)
    .eq('session_games.club_id', clubId);

  if (seasonId) {
    query = query.eq('session_games.season_id', seasonId);
  }
  query = applyMatchupDateFilters(query, dateFrom, dateTo);
  query = applyMatchupGameTypeFilter(query, resolvedGameTypeIds);

  const { data: myGames, error: gamesError } = await query;
  if (gamesError) throw gamesError;

  const gameIds = [...new Set((myGames || []).map((row) => row.session_game_id).filter(Boolean))];
  if (gameIds.length === 0) {
    return { games: [], hasActiveClubMembershipByPlayerId: {} };
  }

  const { data: allPlayers, error: playersError } = await supabase
    .from('players_session_games')
    .select(`
      player_id,
      team_number,
      pair_type,
      session_game_id,
      session_games!inner(
        id,
        winning_team,
        game_type_id
      ),
      players:player_id(
        ${MATCHUP_SEARCH_PLAYER_FIELDS}
      )
    `)
    .in('session_game_id', gameIds);

  if (playersError) throw playersError;

  const uniquePlayerIds = [
    ...new Set((allPlayers || []).map((row) => row.player_id).filter(Boolean)),
  ];

  if (uniquePlayerIds.length === 0) {
    return { games: [], hasActiveClubMembershipByPlayerId: {} };
  }

  const { data: memberships, error: membershipError } = await supabase
    .from('players_club')
    .select('player_id')
    .in('player_id', uniquePlayerIds)
    .eq('club_id', clubId)
    .eq('is_active', true);

  if (membershipError) throw membershipError;

  const hasActiveClubMembershipByPlayerId = {};
  (memberships || []).forEach((row) => {
    hasActiveClubMembershipByPlayerId[row.player_id] = true;
  });

  const byGame = {};
  (allPlayers || []).forEach((row) => {
    const gameId = row.session_game_id;
    if (!byGame[gameId]) {
      byGame[gameId] = {
        id: gameId,
        gameTypeId: row.session_games?.game_type_id,
        pairType: row.pair_type,
        winningTeam: row.session_games?.winning_team,
        players: [],
      };
    }
    if (row.players?.id) {
      byGame[gameId].players.push({
        ...row.players,
        teamNumber: row.team_number,
      });
    }
  });

  return {
    games: Object.values(byGame),
    hasActiveClubMembershipByPlayerId,
  };
};


/**
 * Get list of partnerships for H2H selection
 * @param {string} currentPlayerId - Current player's ID
 * @param {string} clubId - Club ID to filter by
 * @returns {Promise<Array>} List of potential partnership opponents
 */
export const fetchPotentialPartnershipOpponents = async (currentPlayerId, clubId) => {
  try {
    // Get all partnerships
    const { data: allPartnerships, error: allPartnershipsError } = await supabase
      .from('partnerships_with_stats')
      .select('*');

    if (allPartnershipsError) throw allPartnershipsError;

    // Filter out partnerships that include the current player
    const opponentPartnerships = allPartnerships.filter(partnership => 
      partnership.player1_id !== currentPlayerId && partnership.player2_id !== currentPlayerId
    );

    if (opponentPartnerships.length === 0) {
      return [];
    }

    // Get player details for all partnerships
    const allPlayerIds = [
      ...new Set([
        ...opponentPartnerships.map(p => p.player1_id),
        ...opponentPartnerships.map(p => p.player2_id)
      ])
    ];

    const { data: players, error: playersError } = await supabase
      .from('players')
      .select('id, first_name, last_name, gender')
      .in('id', allPlayerIds);

    if (playersError) throw playersError;

    // Create a map for quick player lookup
    const playersMap = new Map(players.map(player => [player.id, player]));

    // Format partnership names
    const formattedPartnerships = opponentPartnerships.map(partnership => {
      const player1 = playersMap.get(partnership.player1_id);
      const player2 = playersMap.get(partnership.player2_id);
      
      if (!player1 || !player2) {
        return null; // Skip if player data is missing
      }

      return {
        id: partnership.id,
        name: `${player1.first_name} ${player1.last_name} & ${player2.first_name} ${player2.last_name}`,
        player1: player1,
        player2: player2,
        chemistryScore: partnership.chemistry_score,
        totalGames: partnership.total_games,
        winRate: partnership.total_games > 0 ? (partnership.total_wins / partnership.total_games) * 100 : 0
      };
    }).filter(Boolean); // Remove null entries

    return formattedPartnerships.sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error('Error fetching potential partnership opponents:', error);
    throw error;
  }
};

/**
 * Calculate individual H2H statistics
 * @param {Array} games - Array of games where both players participated
 * @param {string} player1Id - First player's ID
 * @param {string} player2Id - Second player's ID
 * @returns {Object} H2H statistics
 */
const sortGamePairsNewestFirst = (games) =>
  [...games].sort(
    (a, b) =>
      new Date(b[0]?.session_games?.created_at || 0) -
      new Date(a[0]?.session_games?.created_at || 0)
  );

const calculateIndividualH2HStats = (games, player1Id, player2Id) => {
  let player1Wins = 0;
  let player2Wins = 0;
  const recentResults = [];

  sortGamePairsNewestFirst(games).forEach(gamePair => {
    const player1Game = gamePair.find(g => g.player_id === player1Id);
    const player2Game = gamePair.find(g => g.player_id === player2Id);

    if (player1Game && player2Game) {
      // Since we've already filtered for opposing teams, we can use the result directly
      if (player1Game.result === 'win') {
        player1Wins++;
        recentResults.push('W');
      } else if (player1Game.result === 'loss') {
        player2Wins++;
        recentResults.push('L');
      }
      // Note: We don't handle 'draw' case as it's not common in badminton
    }
  });

  const totalGames = games.length;
  const player1WinRate = totalGames > 0 ? (player1Wins / totalGames) * 100 : 0;
  const player2WinRate = totalGames > 0 ? (player2Wins / totalGames) * 100 : 0;

  return {
    player1Wins,
    player2Wins,
    player1WinRate,
    player2WinRate,
    recentResults: recentResults.slice(0, 5),
  };
};

/**
 * Get player name for display
 * @param {Object} player - Player object
 * @returns {string} Formatted player name
 */
export const getPlayerDisplayName = (player) => {
  if (!player) return 'Unknown Player';
  const full = `${player.first_name || ''} ${player.last_name || ''}`.trim();
  return full || 'Unknown Player';
};

/**
 * Fetch head-to-head record between two teams (4 players)
 * @param {string} player1Id - First player on team 1
 * @param {string} player2Id - Second player on team 1
 * @param {string} player3Id - First player on team 2
 * @param {string} player4Id - Second player on team 2
 * @param {string} clubId - Club ID to filter by
 * @param {string} seasonId - Optional season ID to filter by
 * @param {string} [gameType='all'] - legacy pair-type filter
 * @param {Object} [dateOpts] - { dateFrom, dateTo, balancedOnly, gameTypeIds }
 * @returns {Promise<Object>} Team H2H record data
 */
export const fetchTeamHeadToHead = async (
  player1Id,
  player2Id,
  player3Id,
  player4Id,
  clubId,
  seasonId = null,
  gameType = 'all',
  dateOpts = {}
) => {
  const { dateFrom = null, dateTo = null, balancedOnly = false, gameTypeIds } = dateOpts;
  const resolvedGameTypeIds = resolveMatchupGameTypeIds({
    gameTypeIds,
    gameType,
    balancedOnly,
  });
  try {
    console.log('fetchTeamHeadToHead called with:', {
      player1Id,
      player2Id,
      player3Id,
      player4Id,
      clubId,
      seasonId,
      gameType,
      dateFrom,
      dateTo,
    });
    
    // Get all games where any of these 4 players participated
    let query = supabase
      .from('players_session_games')
      .select(`
        *,
        session_games!inner(
          id,
          session_id,
          club_id,
          season_id,
          created_at,
          team_1_score,
          team_2_score,
          winning_team,
          game_type_id
        )
      `)
      .in('player_id', [player1Id, player2Id, player3Id, player4Id])
      .eq('session_games.club_id', clubId);

    if (seasonId) {
      query = query.eq('session_games.season_id', seasonId);
    }
    query = applyMatchupDateFilters(query, dateFrom, dateTo);
    query = applyMatchupGameTypeFilter(query, resolvedGameTypeIds);

    const { data: allGames, error } = await query;
    console.log('Team H2H query result:', { allGames: allGames?.length, error });

    if (error) throw error;

    // Group games by session_game_id to find games where both teams participated
    const gameGroups = {};
    allGames.forEach(game => {
      const gameId = game.session_game_id;
      if (!gameGroups[gameId]) {
        gameGroups[gameId] = [];
      }
      gameGroups[gameId].push(game);
    });

    // Filter to games where both teams participated (all 4 players in the same game)
    let teamH2hGames = Object.values(gameGroups).filter((games) => {
      const playerIds = games.map((g) => g.player_id);
      return (
        playerIds.includes(player1Id) &&
        playerIds.includes(player2Id) &&
        playerIds.includes(player3Id) &&
        playerIds.includes(player4Id)
      );
    });
    teamH2hGames = filterGameGroupsByGameTypeIds(teamH2hGames, resolvedGameTypeIds);

    console.log('Team H2H games found:', teamH2hGames.length);

    // Fetch ELO data for all games
    const gameIds = teamH2hGames.map(games => games[0].session_game_id);
    const eloData = await fetchTeamEloDataForGames(gameIds, [player1Id, player2Id, player3Id, player4Id], clubId);

    // Get all unique player IDs from all games to fetch player details
    const allPlayerIds = new Set();
    teamH2hGames.forEach(games => {
      games.forEach(game => {
        allPlayerIds.add(game.player_id);
      });
    });
    const playerDetails = await fetchPlayerDetails(Array.from(allPlayerIds));

    // Calculate team H2H statistics
    const teamH2hStats = calculateTeamH2HStats(teamH2hGames, player1Id, player2Id, player3Id, player4Id);

    const result = {
      team1: { player1Id, player2Id },
      team2: { player3Id, player4Id },
      totalGames: teamH2hGames.length,
      ...teamH2hStats,
      games: teamH2hGames.map(games => {
        const team1Player1Game = games.find(g => g.player_id === player1Id);
        const team1Player2Game = games.find(g => g.player_id === player2Id);
        const team2Player1Game = games.find(g => g.player_id === player3Id);
        const team2Player2Game = games.find(g => g.player_id === player4Id);
        const gameId = games[0].session_game_id;
        
        // Get ELO data for this specific game
        const gameEloData = eloData[gameId] || {};
        const team1Player1Elo = gameEloData[player1Id];
        const team1Player2Elo = gameEloData[player2Id];
        const team2Player1Elo = gameEloData[player3Id];
        const team2Player2Elo = gameEloData[player4Id];
        
        // Calculate team average ELO
        const team1AvgElo = team1Player1Elo && team1Player2Elo ? 
          Math.round((team1Player1Elo.preGameElo + team1Player2Elo.preGameElo) / 2) : null;
        const team2AvgElo = team2Player1Elo && team2Player2Elo ? 
          Math.round((team2Player1Elo.preGameElo + team2Player2Elo.preGameElo) / 2) : null;
        
        // Calculate team ELO changes
        const team1EloChange = team1Player1Elo && team1Player2Elo ? 
          (team1Player1Elo.eloChange + team1Player2Elo.eloChange) : null;
        const team2EloChange = team2Player1Elo && team2Player2Elo ? 
          (team2Player1Elo.eloChange + team2Player2Elo.eloChange) : null;
        
        // Get player details for team composition display
        const team1Player1Details = playerDetails[player1Id];
        const team1Player2Details = playerDetails[player2Id];
        const team2Player1Details = playerDetails[player3Id];
        const team2Player2Details = playerDetails[player4Id];
        
        return {
          gameId: gameId,
          date: games[0].session_games.created_at,
          sessionId: games[0].session_games?.session_id || null,
          session_id: games[0].session_games?.session_id || null,
          club_id: games[0].session_games?.club_id || null,
          clubId: games[0].session_games?.club_id || null,
          pairType: games[0].pair_type,
          gameTypeId: games[0].session_games?.game_type_id ?? null,
          team1Result: getTeamResult(games, player1Id, player2Id),
          team2Result: getTeamResult(games, player3Id, player4Id),
          score: `${games[0].session_games.team_1_score}-${games[0].session_games.team_2_score}`,
          winningTeam: games[0].session_games.winning_team,
          // Team composition
          team1Player1: team1Player1Details,
          team1Player2: team1Player2Details,
          team2Player1: team2Player1Details,
          team2Player2: team2Player2Details,
          // Add actual team numbers for the selected teams in this game
          team1PlayersInGameTeamNumber: team1Player1Game?.team_number,
          team2PlayersInGameTeamNumber: team2Player1Game?.team_number,
          // ELO data
          team1AvgElo: team1AvgElo,
          team2AvgElo: team2AvgElo,
          team1EloChange: team1EloChange,
          team2EloChange: team2EloChange,
          // Individual player ELO data for detailed view
          team1Player1Elo: team1Player1Elo?.preGameElo || null,
          team1Player2Elo: team1Player2Elo?.preGameElo || null,
          team2Player1Elo: team2Player1Elo?.preGameElo || null,
          team2Player2Elo: team2Player2Elo?.preGameElo || null,
          // Game intensity calculation
          scoreDifference: Math.abs(games[0].session_games.team_1_score - games[0].session_games.team_2_score),
          isCloseGame: Math.abs(games[0].session_games.team_1_score - games[0].session_games.team_2_score) <= 3
        };
      })
    };

    console.log('fetchTeamHeadToHead returning:', result);
    return result;
  } catch (error) {
    console.error('Error fetching team head-to-head:', error);
    throw error;
  }
};

/**
 * Calculate team H2H statistics
 * @param {Array} games - Array of games where both teams participated
 * @param {string} team1Player1 - First player on team 1
 * @param {string} team1Player2 - Second player on team 1
 * @param {string} team2Player1 - First player on team 2
 * @param {string} team2Player2 - Second player on team 2
 * @returns {Object} Team H2H statistics
 */
const calculateTeamH2HStats = (games, team1Player1, team1Player2, team2Player1, team2Player2) => {
  let team1Wins = 0;
  let team2Wins = 0;
  const recentResults = [];

  sortGamePairsNewestFirst(games).forEach(gamePair => {
    const team1Result = getTeamResult(gamePair, team1Player1, team1Player2);
    const team2Result = getTeamResult(gamePair, team2Player1, team2Player2);

    if (team1Result === 'win') {
      team1Wins++;
      recentResults.push('W');
    } else if (team2Result === 'win') {
      team2Wins++;
      recentResults.push('L');
    }
  });

  const totalGames = games.length;
  const team1WinRate = totalGames > 0 ? (team1Wins / totalGames) * 100 : 0;
  const team2WinRate = totalGames > 0 ? (team2Wins / totalGames) * 100 : 0;

  return {
    team1Wins,
    team2Wins,
    team1WinRate,
    team2WinRate,
    recentResults: recentResults.slice(0, 5),
  };
};

/**
 * Get team result from game data
 * @param {Array} gamePair - Array of player game records for the same session game
 * @param {string} player1Id - First player on the team
 * @param {string} player2Id - Second player on the team
 * @returns {string} 'win' or 'loss'
 */
const getTeamResult = (gamePair, player1Id, player2Id) => {
  const player1Game = gamePair.find(g => g.player_id === player1Id);
  const player2Game = gamePair.find(g => g.player_id === player2Id);
  
  if (player1Game && player2Game) {
    // Both players should have the same result since they're on the same team
    return player1Game.result || player2Game.result;
  }
  
  return 'loss'; // Default to loss if we can't determine
};

/**
 * Get player gender for styling
 * @param {Object} player - Player object
 * @returns {string} Player gender
 */
export const getPlayerGender = (player) => {
  return player?.gender || 'OTHER';
};

/**
 * All-time head-to-head record vs a set of opponents, for the Session Summary
 * tab's "Opponents this session" list. Reuses fetchIndividualHeadToHead (one
 * call per opponent, in parallel) rather than a new SQL aggregate — the
 * opponent set for a single session is small (typically a handful of players).
 *
 * @param {string} playerId - the subject player (not necessarily the viewer)
 * @param {Array<string>} opponentIds - unique opponent player ids
 * @param {string} clubId
 * @returns {Promise<Object>} map of opponentId -> { wins, losses, totalGames }
 */
export const fetchOpponentAllTimeRecords = async (playerId, opponentIds, clubId) => {
  const uniqueIds = Array.from(new Set((opponentIds || []).filter(Boolean)));
  if (!playerId || !clubId || !uniqueIds.length) return {};

  const results = await Promise.all(
    uniqueIds.map(async (opponentId) => {
      try {
        const result = await fetchIndividualHeadToHead(
          playerId,
          opponentId,
          clubId,
          null,
          'all',
          { balancedOnly: false }
        );
        return [opponentId, {
          wins: result.player1Wins || 0,
          losses: result.player2Wins || 0,
          totalGames: result.totalGames || 0,
        }];
      } catch (error) {
        console.error('[fetchOpponentAllTimeRecords] Failed for opponent', opponentId, error);
        return [opponentId, null];
      }
    })
  );

  return results.reduce((map, [opponentId, record]) => {
    if (record) map[opponentId] = record;
    return map;
  }, {});
};
